import { format } from "date-fns/esm";
import React from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";

import {
  PlusIconMinus,
  PlusIconPlus,
} from "../../../../components/svg/PlusIcon";
import Table from "../../../../components/Table";
import SongSuggestionDetails from "./SongSuggestionDetails";

// Move the Cell components outside of SuggestionsTable
function ImageCell({ row }) {
  let image = null;
  if (row?.values?.images && row?.values?.source === "Spotify") {
    image = JSON.parse(row?.values?.images)?.[2]?.url;
  }
  else if (
    row?.values?.images
    && (row?.values?.source === "TuneCore" || row?.values?.source === "TuneCore & Spotify")
  ) {
    image = row?.values?.images.small_url;
  }

  return (
    <div className="hidden w-10 h-10 bg-gray-700 lg:inline-block">
      {image ? (
        <img
          alt="thumb"
          title="thumb"
          src={image}
          className="inline-block w-10 h-10 bg-gray-700"
        />
      ) : (
        <div className="inline-block w-10 h-10 bg-gray-700" />
      )}
    </div>
  );
}

function ReleaseDateCell({ row }) {
  return row?.values?.release_date && row?.values?.release_date !== "0000-00-00"
    ? format(Date.parse(row.values.release_date), "yyyy-MM-dd")
    : null;
}

function ArtistsCell({ row }) {
  return row?.values?.artists.replace(/,/g, ", ").replace(/&#44;/g, ",");
}

function ExpanderCell({ row, isTunecore, highlightColour }) {
  return (
    <>
      {isTunecore ? (
        <span className="float-right">
          {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      ) : (
        <span className="float-right">
          {row.isExpanded ? (
            <PlusIconMinus className="h-4" fill={highlightColour} />
          ) : (
            <PlusIconPlus className="h-4" fill={highlightColour} />
          )}
        </span>
      )}
    </>
  );
}

// Create a factory function that returns a component with the right props
function createExpanderCellWithProps(isTunecore, highlightColour) {
  // This returns a component that will be used as the Cell
  function ExpanderCellWithProps(props) {
    return (
      <ExpanderCell
        {...props}
        isTunecore={isTunecore}
        highlightColour={highlightColour}
      />
    );
  }
  return ExpanderCellWithProps;
}

// Our table component
function SuggestionsTable({ data, highlightColour }) {
  const { t } = useTranslation("songs");

  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Create the ExpanderCellWithProps component outside of useMemo
  const ExpanderCellWithProps = React.useMemo(
    () => createExpanderCellWithProps(isTunecore, highlightColour),
    [isTunecore, highlightColour],
  );

  const columns = React.useMemo(
    () => [
      {
        Header: () => null,
        accessor: "images",
        width: "80",
        Cell: ImageCell,
      },
      {
        Header: t("songs:spotify-table-name"),
        accessor: "title",
        width: "25%",
      },
      {
        Header: t("songs:release-date"),
        accessor: "release_date",
        width: "20%",
        Cell: ReleaseDateCell,
      },
      {
        Header: t("songs:spotify-table-performers"),
        accessor: "artists",
        width: "25%",
        Cell: ArtistsCell,
      },
      {
        Header: t("songs:spotify-table-suggested-by"),
        accessor: "source",
        width: "20%",
      },
      {
        // Make an expander cell
        Header: () => null, // No header
        id: "expander", // It needs an ID
        width: "7%",
        Cell: ExpanderCellWithProps,
        // We can override the cell renderer with a SubCell to be used with an expanded row
        SubCell: () => null, // No expander on an expanded row
      },
    ],
    [ExpanderCellWithProps, t],
  );

  return (
    <Table
      data={data.sort((a, b) => a.popularity - b.popularity)}
      sortBy={{}}
      columns={columns}
      highlightColour={highlightColour}
      searchSuffix={t("common:songs-search-suffix")}
      min={5}
      getRowId={React.useCallback(row => row.isrc, [])}
      noneMessage={t("songs:spotify-table-none-message")}
      revealComponent={<SongSuggestionDetails />}
    />
  );
}

export default SuggestionsTable;
